import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import * as menu from "@zag-js/menu";
import { useMachine, normalizeProps, Portal } from "@zag-js/react";
import { match } from "ts-pattern";
import { twMerge } from "tailwind-merge";
import { isNullable } from "effect/Predicate";
import { ButtonVariant, getButtonClassNames } from "../button";
import { CheckmarkIcon } from "../../icons";
import { useDescriptionVariant } from "../description/use-description-variant";
import { Tooltip, TooltipContent, TooltipOrigin } from "../tooltip";
import {
  StaticDescriptionContent,
  TooltipDescriptionContent,
} from "../description";
import { getValidChildren } from "../../utils/get-valid-children";
import { FlippingChevron } from "../flipping-chevron";
import { cn } from "../../utils/class-names";
import { tv } from "tailwind-variants";
import { UnstyledLink } from "../link";

interface MenuContext {
  api: ReturnType<typeof menu.connect>;
  selectedId: string;
  hasSingleChild: boolean;
  setHasSingleChild: Dispatch<SetStateAction<boolean>>;
  disabled: boolean;
}

const MenuContext = createContext<MenuContext | null>(null);

const useMenuContext = () => {
  const menuContext = useContext(MenuContext);

  if (isNullable(menuContext)) {
    throw new Error(
      "Menu context is null. Only consume useMenuContext within the Menu component."
    );
  }

  return menuContext;
};

interface MenuProps {
  id: string;
  selectedId: string;
  setSelectedId: (id: string) => void;
  onSelect?: (id: string) => void;
  ariaLabel?: string;
  disabled?: boolean;
  children?: ReactNode;
}

export const Menu = ({
  id,
  selectedId,
  setSelectedId,
  onSelect,
  ariaLabel,
  disabled = false,
  children,
}: MenuProps): JSX.Element => {
  const uniqueId = useId();
  const menuId = `${id}-${uniqueId}`;

  const _onSelect = useCallback(
    (value: string) => {
      setSelectedId(value);
      onSelect?.(value);
    },
    [setSelectedId, onSelect]
  );

  const [state, send] = useMachine(
    menu.machine({
      id: menuId,
      "aria-label": ariaLabel,
    }),
    {
      context: {
        onSelect: ({ value }) => _onSelect(value),
      },
    }
  );

  const api = menu.connect(state, send, normalizeProps);

  const [hasSingleChild, setHasSingleChild] = useState(false);

  return (
    <div>
      <MenuContext.Provider
        value={{ api, selectedId, hasSingleChild, setHasSingleChild, disabled }}
      >
        {children}
      </MenuContext.Provider>
    </div>
  );
};

interface MenuButtonProps {
  variant: MenuButtonVariant;
  hideCaret?: boolean;
  className?: string;
  "data-cy"?: string;
  children?: ReactNode;
}

type MenuOnlyVariant = "modal" | "form";
export type MenuButtonVariant = ButtonVariant | MenuOnlyVariant;

export const MenuButton = ({
  variant,
  hideCaret = false,
  className,
  children,
  "data-cy": dataCy,
}: MenuButtonProps) => {
  const { api, hasSingleChild, disabled } = useMenuContext();

  const menuButtonClassName = useMemo(() => {
    const getVariantClasses = () => {
      if (variant === "form" || variant === "modal") {
        const state = match({ isOpen: api.isOpen, disabled })
          .with({ disabled: true }, () => "disabled" as const)
          .with({ isOpen: true }, () => "selected" as const)
          .otherwise(() => "unselected" as const);

        return twMerge(
          menuOnlyVariantClassMap[variant]["all"],
          menuOnlyVariantClassMap[variant][state]
        );
      }

      return getButtonClassNames(variant, "normal", disabled);
    };

    return twMerge(
      "flex items-center justify-between gap-x-2",
      getVariantClasses(),
      "font-normal",
      disabled ? "opacity-50" : "",
      className
    );
  }, [variant, api.isOpen, disabled, className]);

  if (hasSingleChild) {
    return (
      <div
        className={`pointer-events-none inline-block ${menuButtonClassName}`}
        data-cy={dataCy}
      >
        {children}
      </div>
    );
  }

  const disabledProps = {
    disabled: true,
  };

  return (
    <button
      className={menuButtonClassName}
      {...(disabled ? disabledProps : api.triggerProps)}
      data-cy={dataCy}
    >
      <span>{children}</span>
      {!hideCaret && <Caret />}
    </button>
  );
};

const menuOnlyVariantClassMap = {
  modal: {
    all: "rounded-lg hover:ease-in duration-100 transition-colors hover:cursor-pointer",
    unselected: "text-shale-200 bg-shale-600 hover:bg-shale-500 px-4 py-2.5",
    selected:
      "bg-transparent text-shale-200 border border-2 border-blue-400 px-[14px] py-2",
    disabled: "bg-transparent px-4 py-2.5",
  },
  form: {
    all: "rounded-lg hover:ease-in duration-100 transition-colors hover:cursor-pointer",
    unselected:
      "bg-shale-700 text-white hover:bg-shale-600 active:bg-shale-750 px-2.5 py-2.5",
    selected: "bg-shale-700 text-white border border-2 border-blue-400 p-2",
    disabled: "bg-shale-750 text-shale-400 p-2.5 pointer-events-none",
  },
};

interface MenuTriggerProps {
  ["data-cy"]?: string;
  children?: ReactNode;
}

export const MenuTrigger = ({
  children,
  "data-cy": dataCy,
}: MenuTriggerProps): JSX.Element => {
  const { api, hasSingleChild, disabled } = useMenuContext();
  const className = `text-white font-sans text-xl font-medium flex items-center justify-between gap-x-2 ${
    disabled ? "opacity-50" : ""
  }`;

  if (hasSingleChild) {
    return (
      <div className={className} data-cy={dataCy}>
        {children}
      </div>
    );
  }

  const disabledProps = {
    disabled: true,
  };

  return (
    <button
      className={className}
      data-cy={dataCy}
      {...(disabled ? disabledProps : api.triggerProps)}
    >
      <span>{children}</span>
      <Caret />
    </button>
  );
};

const Caret: FC = () => {
  const {
    api: { isOpen },
  } = useMenuContext();

  return (
    <>
      <span aria-hidden>
        <FlippingChevron isOpen={isOpen} />
      </span>
    </>
  );
};

interface MenuListProps {
  className?: string;
  minWidth?: number;
  children?: ReactNode;
}

export const MenuList = ({
  className,
  minWidth = 200,
  children,
}: MenuListProps): JSX.Element => {
  const { api, hasSingleChild, setHasSingleChild } = useMenuContext();

  useEffect(() => {
    setHasSingleChild(getValidChildren(children).length < 2);
  }, [children, setHasSingleChild]);

  if (hasSingleChild) return <></>;

  return (
    <Portal>
      <div
        className={cn("z-10", className)}
        {...api.positionerProps}
        /**
         * May need to investigate this. It seems that the zag menu translates
         * the positioner 8px from the left edge of the viewport. This max-width
         * ensures that the menu does not exceed the viewport width, and stays
         * centered.
         */
        style={{
          maxWidth: "calc(100vw - 16px)",
        }}
      >
        <ul
          className={cn(
            "rounded-lg bg-shale-750 shadow-lg max-w-screen-xs",
            "data-[state=open]:animate-fade-up-enter data-[state=closed]:animate-fade-up-exit"
          )}
          style={{ minWidth }}
          data-state={api.isOpen ? "open" : "closed"}
          {...api.contentProps}
        >
          {children}
        </ul>
      </div>
    </Portal>
  );
};

interface MenuItemProps {
  id: string;
  ["data-cy"]?: string;
  description?: ReactNode;
  children?: ReactNode;
}

const baseMenuItem = tv({
  base: "flex flex-col gap-2.5 px-5 py-4 sm:py-2.5 first:pt-5 first:rounded-t-lg last:pb-5 last:rounded-b-lg cursor-pointer hover:bg-shale-600 hover:ease-in duration-100",
});

type BaseMenuItemProps = MenuItemProps & {
  className?: string;
  selectedClassName?: string;
  children?: ReactNode;
};

const BaseMenuItem = ({
  id,
  className,
  selectedClassName,
  children,
  "data-cy": dataCy,
  description,
}: BaseMenuItemProps): JSX.Element => {
  const { api, selectedId } = useMenuContext();
  const isSelected = id === selectedId;
  const selectedStyles = `text-white ${selectedClassName}`;
  const content = (
    <div
      className={`flex items-center gap-x-2.5 justify-between ${
        isSelected ? selectedStyles : "text-shale-200"
      }`}
    >
      <div>{children}</div>
      {isSelected && (
        <span>
          <CheckmarkIcon className="w-6 h-6 justify-self-end" aria-hidden />
        </span>
      )}
    </div>
  );

  const descriptionVariant = useDescriptionVariant();
  const showContentWithDescription = !!description && api.isOpen;

  /**
   * TODO: on mobile devices (at least the google chrome mobile device
   * simulator), getting a "click through" scenario, where a click event on the
   * menu is clicking a link underneath the menu as well - need to fix at some
   * point.
   */
  return (
    <li
      className={baseMenuItem({ className })}
      key={id}
      {...api.getItemProps({ id })}
      data-cy={dataCy}
    >
      {showContentWithDescription && (
        <>
          <Tooltip off={descriptionVariant === "static"} gutter={20}>
            <TooltipOrigin>{content}</TooltipOrigin>
            <TooltipContent>
              <TooltipDescriptionContent>
                {description}
              </TooltipDescriptionContent>
            </TooltipContent>
          </Tooltip>

          {descriptionVariant === "static" && (
            <StaticDescriptionContent>{description}</StaticDescriptionContent>
          )}
        </>
      )}

      {!showContentWithDescription && content}
    </li>
  );
};

export const MenuItem = (props: MenuItemProps): JSX.Element => (
  <BaseMenuItem
    {...props}
    className="text-base font-normal"
    selectedClassName="!font-medium"
  />
);

export const MenuTriggerItem = (props: MenuItemProps): JSX.Element => (
  <BaseMenuItem {...props} className="text-sm font-bold" />
);

interface MenuItemLinkProps {
  id: string;
  href: string;
  children?: ReactNode;
  className?: string;
}

export const MenuItemLink = ({
  id,
  href,
  children,
  className,
}: MenuItemLinkProps): JSX.Element => {
  const { api } = useMenuContext();

  return (
    <UnstyledLink
      {...api.getItemProps({ id })}
      external
      className={baseMenuItem({ className: cn("text-shale-200", className) })}
      href={href}
    >
      {children}
    </UnstyledLink>
  );
};
